import { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getAnalytics } from '../lib/api';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Eye, Store, Utensils, TrendingUp, Smartphone, Monitor, Tablet } from 'lucide-react';

const DEVICE_COLORS = { mobile: '#C9A227', desktop: '#C75D4E', tablet: '#4F7C61', unknown: '#969282' };

export default function Analytics() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                const response = await getAnalytics();
                setAnalytics(response.data);
            } catch {
                // Analytics fetch failed, will show empty state
            } finally {
                setLoading(false);
            }
        };
        fetchAnalytics();
    }, []);

    const statCards = [
        { label: 'Total Views', value: analytics?.total_views || 0, icon: Eye, color: '#C75D4E' },
        { label: 'Restaurants', value: analytics?.total_restaurants || 0, icon: Store, color: '#4F7C61' },
        { label: 'Total Dishes', value: analytics?.total_dishes || 0, icon: Utensils, color: '#D2924E' },
    ];

    const chartData = analytics?.restaurants?.map(r => ({
        name: r.name.length > 15 ? r.name.substring(0, 15) + '...' : r.name,
        views: r.views || 0
    })) || [];

    // Time-series data - show last 14 days with short labels
    const dailyViews = (analytics?.daily_views || []).slice(-14).map(d => ({
        ...d,
        label: new Date(d.date + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })
    }));

    // Device breakdown for pie chart
    const deviceData = analytics?.device_breakdown
        ? Object.entries(analytics.device_breakdown)
            .filter(([_, v]) => v > 0)
            .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, fill: DEVICE_COLORS[k] || '#999' }))
        : [];

    const totalEvents = deviceData.reduce((s, d) => s + d.value, 0);

    return (
        <DashboardLayout>
            <div className="space-y-8">
                <div>
                    <h1 className="text-3xl font-semibold text-[#2B2A27] tracking-tight">Analytics</h1>
                    <p className="text-[#69665B] mt-1">Track your menu performance</p>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <Card key={i} className="border-[#E5E1D1]">
                                <CardContent className="p-6">
                                    <div className="h-20 bg-[#F0EDDF] rounded-xl animate-pulse" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {statCards.map((stat) => {
                                const Icon = stat.icon;
                                return (
                                    <Card key={stat.label} className="border-[#E5E1D1] shadow-sm">
                                        <CardContent className="p-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-[#69665B]">{stat.label}</p>
                                                    <p className="text-3xl font-semibold text-[#2B2A27] mt-1">
                                                        {stat.value.toLocaleString()}
                                                    </p>
                                                </div>
                                                <div 
                                                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                                                    style={{ backgroundColor: `${stat.color}15` }}
                                                >
                                                    <Icon className="w-6 h-6" style={{ color: stat.color }} />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Time-series views chart */}
                        <Card className="border-[#E5E1D1] shadow-sm">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-[#C75D4E]" />
                                    Views Over Time (Last 14 Days)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-72" data-testid="daily-views-chart">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={dailyViews} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                                            <defs>
                                                <linearGradient id="menuGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#C75D4E" stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor="#C75D4E" stopOpacity={0}/>
                                                </linearGradient>
                                                <linearGradient id="dishGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#C9A227" stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor="#C9A227" stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D1" />
                                            <XAxis dataKey="label" tick={{ fill: '#69665B', fontSize: 11 }} angle={-35} textAnchor="end" height={50} />
                                            <YAxis tick={{ fill: '#69665B' }} allowDecimals={false} />
                                            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E1D1', borderRadius: '12px' }} />
                                            <Legend verticalAlign="top" height={36} />
                                            <Area type="monotone" dataKey="menu_views" name="Menu Views" stroke="#C75D4E" fill="url(#menuGrad)" strokeWidth={2} />
                                            <Area type="monotone" dataKey="dish_views" name="Dish Views" stroke="#C9A227" fill="url(#dishGrad)" strokeWidth={2} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Row: Device Breakdown + Restaurant Bar Chart */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Device Breakdown */}
                            <Card className="border-[#E5E1D1] shadow-sm">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Smartphone className="w-5 h-5 text-[#C9A227]" />
                                        Device Breakdown
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {totalEvents > 0 ? (
                                        <div className="flex items-center gap-6" data-testid="device-breakdown">
                                            <div className="h-48 w-48 flex-shrink-0">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie data={deviceData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={4} dataKey="value">
                                                            {deviceData.map((entry, idx) => (
                                                                <Cell key={idx} fill={entry.fill} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip contentStyle={{ borderRadius: '12px' }} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                            <div className="space-y-3 flex-1">
                                                {deviceData.map(d => {
                                                    const pct = ((d.value / totalEvents) * 100).toFixed(0);
                                                    const Icon = d.name === 'Mobile' ? Smartphone : d.name === 'Tablet' ? Tablet : Monitor;
                                                    return (
                                                        <div key={d.name} className="flex items-center gap-3">
                                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill }} />
                                                            <Icon className="w-4 h-4 text-[#69665B]" />
                                                            <span className="text-sm text-[#2B2A27] flex-1">{d.name}</span>
                                                            <span className="text-sm font-semibold text-[#2B2A27]">{pct}%</span>
                                                            <span className="text-xs text-[#969282]">({d.value})</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-[#969282]">
                                            <Monitor className="w-10 h-10 mx-auto mb-3 opacity-40" />
                                            <p className="text-sm">No device data yet. Views will appear once customers visit your menus.</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Restaurant views bar chart */}
                            {chartData.length > 0 && (
                                <Card className="border-[#E5E1D1] shadow-sm">
                                    <CardHeader>
                                        <CardTitle>Views by Restaurant</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-56">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D1" />
                                                    <XAxis dataKey="name" tick={{ fill: '#69665B', fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
                                                    <YAxis tick={{ fill: '#69665B' }} allowDecimals={false} />
                                                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E1D1', borderRadius: '12px' }} />
                                                    <Bar dataKey="views" fill="#C75D4E" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* Top Dishes */}
                        {analytics?.top_dishes?.length > 0 && (
                            <Card className="border-[#E5E1D1] shadow-sm">
                                <CardHeader>
                                    <CardTitle>Top Viewed Dishes</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {analytics.top_dishes.map((dish, index) => (
                                            <div 
                                                key={dish.id} 
                                                className="flex items-center justify-between p-4 bg-[#F0EDDF]/50 rounded-xl"
                                                data-testid={`top-dish-${index}`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <span className="w-8 h-8 rounded-full bg-[#C75D4E]/10 text-[#C75D4E] font-semibold flex items-center justify-center text-sm">
                                                        {index + 1}
                                                    </span>
                                                    <div>
                                                        <p className="font-medium text-[#2B2A27]">{dish.name}</p>
                                                        <p className="text-sm text-[#969282]">{dish.restaurant_name}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 text-[#69665B]">
                                                    <Eye className="w-4 h-4" />
                                                    {dish.views || 0}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </DashboardLayout>
    );
}
